import type { KeyboardEvent } from 'react';
import React, { useEffect, useId, useRef, useState } from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { Icon } from '@noths/polaris-design-system-core-components';

import type { ReviewRatingFilter, ReviewsSummaryRatingBreakdown } from 'src/redux/reviews/types';
import * as copy from '../constants/copy';
import * as styles from './RatingFilter.styles';

/** "Show all" first, then highest to lowest, to match the histogram above. */
const OPTIONS: ReviewRatingFilter[] = [null, 5, 4, 3, 2, 1];

export interface RatingFilterProps {
  /** `null` is the unfiltered list. */
  activeRating: ReviewRatingFilter;
  onChange: (rating: ReviewRatingFilter) => void;
  /** Disables a rating nobody has left; optional, so the filter works before the breakdown settles. */
  starCounts?: ReviewsSummaryRatingBreakdown['starCounts'];
}

const optionLabel = (rating: ReviewRatingFilter) =>
  rating === null ? copy.FILTER_ALL : copy.ratingFilterLabel(rating);

/**
 * Filters the list to one star rating: a "Filter" button that opens a listbox of the ratings.
 * Exactly one option is always in effect. A rating nobody has left is disabled rather than
 * hidden, so the options stay stable. Closes on a choice, Escape, Tab or a click elsewhere,
 * and hands focus back to the button so the customer is not left nowhere.
 */
export const RatingFilter = ({ activeRating, onChange, starCounts }: RatingFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const listboxId = useId();

  const isDisabled = (rating: ReviewRatingFilter) =>
    rating !== null && starCounts !== undefined && starCounts[rating] === 0;

  const close = (returnFocus = true) => {
    setIsOpen(false);

    if (returnFocus) {
      buttonRef.current?.focus();
    }
  };

  const choose = (rating: ReviewRatingFilter) => {
    if (isDisabled(rating)) {
      return;
    }

    onChange(rating);
    close();
  };

  // The active option takes focus on opening, so arrow keys start from what is in effect.
  useEffect(() => {
    if (isOpen) {
      optionRefs.current[OPTIONS.indexOf(activeRating)]?.focus();
    }
  }, [activeRating, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Closes without moving focus: the customer has clicked somewhere else on purpose.
    const handlePointerDown = ({ target }: PointerEvent) => {
      if (!rootRef.current?.contains(target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  const focusOption = (from: number, step: 1 | -1) => {
    for (let index = from + step; index >= 0 && index < OPTIONS.length; index += step) {
      if (!isDisabled(OPTIONS[index])) {
        optionRefs.current[index]?.focus();

        return;
      }
    }
  };

  const handleOptionKeyDown = (index: number) => (event: KeyboardEvent<HTMLLIElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusOption(index, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusOption(index, -1);
        break;
      case 'Home':
        event.preventDefault();
        focusOption(-1, 1);
        break;
      case 'End':
        event.preventDefault();
        focusOption(OPTIONS.length, -1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(OPTIONS[index]);
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        close(false);
        break;
      default:
    }
  };

  return (
    <div css={styles.root} ref={rootRef}>
      <button
        aria-controls={isOpen ? listboxId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        css={styles.trigger}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        ref={buttonRef}
        type="button"
      >
        {/* The button names the rating in effect, so a filtered list says so at the top. */}
        {activeRating === null ? (
          copy.FILTER_BUTTON
        ) : (
          <>
            <span css={visuallyHidden}>{copy.FILTER_BUTTON}: </span>
            {copy.ratingFilterLabel(activeRating)}
          </>
        )}
        <Icon aria-hidden name="collapsed" size="sm" />
      </button>

      {isOpen && (
        <ul aria-label={copy.FILTER_LABEL} css={styles.listbox} id={listboxId} role="listbox">
          {OPTIONS.map((rating, index) => (
            <li
              aria-disabled={isDisabled(rating) || undefined}
              aria-selected={rating === activeRating}
              css={styles.option}
              key={rating ?? 'all'}
              onClick={() => choose(rating)}
              onKeyDown={handleOptionKeyDown(index)}
              ref={(element) => {
                optionRefs.current[index] = element;
              }}
              role="option"
              tabIndex={isDisabled(rating) ? -1 : 0}
            >
              {optionLabel(rating)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

RatingFilter.displayName = 'RatingFilter';
